import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import { Modal, ModalHeader, ModalBody, Button, Input, Label, Table, Spinner } from "reactstrap";
import { AuthContext } from "../../contexts/AuthContext";

const URL_TC = process.env.REACT_APP_URL_TIPOS_CAMBIO;
const CACHE = "tc-vigente";
const auth = () => ({ Authorization: `Bearer: ${localStorage.getItem("app_token")}` });
const fmt = (v) => (v ? Number(v).toFixed(4) : "—");
const fechaCorta = (f) => (f ? moment(f, "YYYY-MM-DD").format("DD MMM") : "");

// Tipo de cambio vigente en el sidebar (arriba del selector de idioma). Clic = historial y captura.
export default function TipoCambio() {
  const { user } = useContext(AuthContext);
  const [tc, setTc] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(CACHE)) || null; } catch (_) { return null; }
  });
  const [abierto, setAbierto] = useState(false);
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [fecha, setFecha] = useState(moment().format("YYYY-MM-DD"));
  const [valor, setValor] = useState("");

  const guardaVigente = (v) => {
    setTc(v);
    try { sessionStorage.setItem(CACHE, JSON.stringify(v)); } catch (_) {}
  };

  useEffect(() => {
    const hoy = moment().format("YYYY-MM-DD");
    if (tc && tc.fecha === hoy) return; // ya lo tenemos de hoy en esta sesión
    axios.get(`${URL_TC}Vigente`, { headers: auth() }).then(({ data }) => data && guardaVigente(data)).catch(() => {});
  }, []); // eslint-disable-line

  const cargaLista = () => {
    setCargando(true);
    axios
      .get(URL_TC, { headers: auth() })
      .then(({ data }) => setLista(Array.isArray(data) ? data : []))
      .catch(() => setLista([]))
      .finally(() => setCargando(false));
  };

  const abrir = () => { setAbierto(true); cargaLista(); };

  const actualizar = () => {
    setCargando(true);
    axios
      .post(`${URL_TC}Actualizar`, {}, { headers: auth() })
      .then(({ data }) => { guardaVigente(data); cargaLista(); })
      .catch((err) => {
        setCargando(false);
        Swal.fire({ icon: "error", title: "No se pudo actualizar", text: err?.response?.data?.message || err.message });
      });
  };

  const capturar = (e) => {
    e.preventDefault();
    axios
      .post(URL_TC, { fecha, tipoCambio: valor }, { headers: auth() })
      .then(({ data }) => {
        if (data.fecha >= (tc?.fecha || "")) guardaVigente(data);
        setValor("");
        cargaLista();
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: `TC ${data.fecha}: ${fmt(data.tipoCambio)}`, showConfirmButton: false, timer: 1800 });
      })
      .catch((err) => Swal.fire({ icon: "error", title: "No se pudo guardar", text: err?.response?.data?.message || err.message }));
  };

  return (
    <>
      <button type="button" className="sidebar-tc" onClick={abrir} title="Tipo de cambio USD → MXN">
        <span className="sidebar-tc-icon"><i className="fas fa-dollar-sign" /></span>
        <span className="sidebar-tc-body">
          <span className="sidebar-tc-label">TC USD · {fechaCorta(tc?.fecha) || "—"}</span>
          <span className="sidebar-tc-valor">{tc ? `$${fmt(tc.tipoCambio)}` : "Sin registro"}</span>
        </span>
        <span className={`sidebar-tc-fuente${tc?.fuente === "Manual" ? " manual" : ""}`}>{tc ? (tc.fuente === "Manual" ? "Manual" : "Auto") : ""}</span>
      </button>

      <Modal isOpen={abierto} toggle={() => setAbierto(false)}>
        <ModalHeader toggle={() => setAbierto(false)}>
          <i className="fas fa-dollar-sign" style={{ color: "#2563eb", marginRight: 8 }} />
          Tipo de cambio USD → MXN
        </ModalHeader>
        <ModalBody>
          <div className="tc-hero">
            <div>
              <span>Vigente · {tc?.fecha || "—"}</span>
              <strong>${fmt(tc?.tipoCambio)}</strong>
              {tc && tc.fuente !== "Manual" ? (
                <small>Mercado {fmt(tc.mercado)} ({tc.fuente}) + {Number(tc.margen || 0).toFixed(2)}</small>
              ) : tc ? <small>Capturado por {tc.usuario}</small> : null}
            </div>
            <Button size="sm" color="primary" onClick={actualizar} disabled={cargando}>
              {cargando ? <Spinner size="sm" /> : <i className="fas fa-sync-alt" />} Actualizar de mercado
            </Button>
          </div>
          <div className="td-muted" style={{ fontSize: "0.7rem", margin: "6px 0 14px" }}>
            Se registra solo todos los días a las 8:00 (mercado + 4 centavos). Si capturas uno a mano para un día, el automático no lo reemplaza.
          </div>

          {user?.menuAdmin ? (
            <form className="tc-captura" onSubmit={capturar}>
              <div>
                <Label>Fecha</Label>
                <Input bsSize="sm" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required />
              </div>
              <div>
                <Label>Tipo de cambio</Label>
                <Input bsSize="sm" type="number" step="0.0001" placeholder="18.8736" value={valor} onChange={(e) => setValor(e.target.value)} required />
              </div>
              <Button size="sm" color="success" type="submit"><i className="fas fa-save" /> Capturar</Button>
            </form>
          ) : null}

          <div className="tc-lista">
            <Table size="sm" hover>
              <thead>
                <tr><th>Fecha</th><th className="td-num">TC</th><th className="td-num">Mercado</th><th>Fuente</th><th>Usuario</th></tr>
              </thead>
              <tbody>
                {lista.map((x) => (
                  <tr key={x._id}>
                    <td>{x.fecha}</td>
                    <td className="td-num td-strong">{fmt(x.tipoCambio)}</td>
                    <td className="td-num td-muted">{x.mercado ? fmt(x.mercado) : "—"}</td>
                    <td><span className={`badge-soft badge-soft-${x.fuente === "Manual" ? "warning" : "info"}`}>{x.fuente}</span></td>
                    <td className="td-muted">{x.usuario}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {!cargando && lista.length === 0 ? <div className="td-muted">Sin registros</div> : null}
          </div>
        </ModalBody>
      </Modal>
    </>
  );
}
